import { type PointerEvent as RPointerEvent, useEffect, useMemo, useRef, useState } from "react";
import type { BoardSnapshotListResponse, MessageResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { label, shortTime } from "../data/display.ts";
import { mediaUrl } from "../data/media.ts";
import { useStore } from "../data/store.ts";
import { Avatar, colorFor } from "../ui/Avatar.tsx";
import { Icon } from "../ui/Icon.tsx";
import type { IconName } from "../ui/iconPaths.ts";
import { type El, type ElementType, type Style, CUSTOM_SHAPES, FILLS, FONT_EXCALIFONT, NOTE_YELLOW, STROKES, boundText, bounds, bump, create, hits, styleOf, union } from "./model.ts";
import { paths, toDark } from "./render.ts";
import { BoardSession, useBoard } from "./session.ts";
import styles from "./BoardView.module.css";

type Tool = "select" | "hand" | "rectangle" | "ellipse" | "diamond" | "custom" | "note" | "arrow" | "line" | "freedraw" | "text" | "image" | "eraser" | "laser";

const TOOLS: { tool: Tool; icon: IconName; key: string; label: string }[] = [
  { tool: "select", icon: "cursor", key: "v", label: "Auswählen" },
  { tool: "hand", icon: "drag", key: "h", label: "Verschieben" },
  { tool: "rectangle", icon: "rect", key: "r", label: "Rechteck" },
  { tool: "ellipse", icon: "circle", key: "o", label: "Ellipse" },
  { tool: "arrow", icon: "arrow", key: "a", label: "Pfeil" },
  { tool: "freedraw", icon: "edit", key: "p", label: "Stift" },
  { tool: "text", icon: "text", key: "t", label: "Text" },
  { tool: "image", icon: "image", key: "i", label: "Bild" },
  { tool: "eraser", icon: "close", key: "e", label: "Radierer" },
];
const SHAPE_NAMES: Record<string, string> = { diamond: "Raute", line: "Linie", note: "Notizzettel", laser: "Zeigestift", triangle: "Dreieck", parallelogram: "Parallelogramm", trapezoid: "Trapez", pentagon: "Fünfeck", hexagon: "Sechseck", star: "Stern", block_arrow: "Blockpfeil", cylinder: "Zylinder", cloud: "Wolke", document: "Dokument" };

interface View {
  x: number;
  y: number;
  zoom: number;
}

type Drag =
  | { kind: "create"; id: string; x0: number; y0: number }
  | { kind: "move"; x0: number; y0: number; start: Map<string, El> }
  | { kind: "resize"; id: string; corner: string; start: El }
  | { kind: "pan"; sx: number; sy: number; view: View }
  | { kind: "marquee"; x0: number; y0: number; x1: number; y1: number }
  | { kind: "erase" }
  | { kind: "laser"; points: [number, number][] };

/** The canvas, full width (docs/web-designs screen 6). */
export function BoardView({ id, onBack }: { id: string; onBack: (conversationId?: string) => void }) {
  const services = useServices();
  const [session, setSession] = useState<BoardSession | null>(null);
  useEffect(() => {
    const s = new BoardSession(id, services.session, services.realtime);
    setSession(s);
    s.open().catch(() => (s.error = "Das Canvas ließ sich nicht laden."));
    return () => s.close();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!session) return null;
  return <Board session={session} onBack={onBack} />;
}

function Board({ session, onBack }: { session: BoardSession; onBack: (conversationId?: string) => void }) {
  const services = useServices();
  useBoard(session);
  const conversation = useStore((s) => (session.board ? s.conversations.get(session.board.conversation_id) : undefined));
  const dark = matchMedia("(prefers-color-scheme: dark)").matches;
  const color = dark ? toDark : (c: string) => c;
  const [tool, setTool] = useState<Tool>("select");
  const [custom, setCustom] = useState<string>("triangle");
  const [style, setStyle] = useState<Style>("sketch");
  const [selected, setSelected] = useState<string[]>([]);
  const [view, setView] = useState<View>({ x: -80, y: -60, zoom: 1 });
  const [drag, setDrag] = useState<Drag | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [menu, setMenu] = useState<"shapes" | "export" | "versions" | null>(null);
  const [laser, setLaser] = useState<[number, number][]>([]);
  const svg = useRef<SVGSVGElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const spaceDown = useRef(false);
  const elements = session.visible();
  const byId = useMemo(() => new Map(elements.map((e) => [e.id, e])), [elements]);
  const me = services.store.me;

  useEffect(() => {
    const total = union(elements.map(bounds));
    if (total && view.x === -80 && view.y === -60) setView({ x: total.minX - 80, y: total.minY - 80, zoom: 1 });
  }, [elements.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const toCanvas = (cx: number, cy: number): [number, number] => {
    const r = svg.current!.getBoundingClientRect();
    return [view.x + (cx - r.left) / view.zoom, view.y + (cy - r.top) / view.zoom];
  };

  // Text inside a shape belongs to it: a click on it takes the shape.
  const at = (x: number, y: number) => {
    const hit = [...elements].reverse().find((e) => hits(e, x, y, 6 / view.zoom));
    return hit?.containerId ? (byId.get(hit.containerId) ?? hit) : hit;
  };

  function commit(changed: El[]) {
    session.apply(changed);
  }

  function remove(ids: string[]) {
    const out: El[] = [];
    for (const id of ids) {
      const e = session.elements.get(id);
      if (!e || e.isDeleted) continue;
      out.push(bump(e, { isDeleted: true }));
      const t = boundText(e, session.elements);
      if (t) out.push(bump(t, { isDeleted: true }));
    }
    commit(out);
    setSelected([]);
  }

  function setOnSelection(patch: Partial<El>) {
    commit(selected.map((id) => session.elements.get(id)).filter((e): e is El => Boolean(e)).map((e) => bump(e, patch)));
  }

  // ---- pointer ----
  function down(ev: RPointerEvent<SVGSVGElement>) {
    if (editing) return finishText();
    (ev.target as Element).setPointerCapture?.(ev.pointerId);
    const [x, y] = toCanvas(ev.clientX, ev.clientY);
    if (tool === "hand" || spaceDown.current || ev.button === 1) return setDrag({ kind: "pan", sx: ev.clientX, sy: ev.clientY, view });
    if (tool === "laser") return setDrag({ kind: "laser", points: [[x, y]] });
    if (tool === "eraser") {
      const hit = at(x, y);
      if (hit) remove([hit.id]);
      return setDrag({ kind: "erase" });
    }
    if (tool === "image") return picker.current?.click();
    if (tool === "text") {
      const e = create("text", { x, y }, style, session.elements.values());
      commit([e]);
      setEditing(e.id);
      setTool("select");
      return;
    }
    if (tool === "select") {
      const handle = (ev.target as Element).getAttribute("data-handle");
      if (handle && selected.length === 1) {
        const e = session.elements.get(selected[0]!)!;
        return setDrag({ kind: "resize", id: e.id, corner: handle, start: e });
      }
      const hit = at(x, y);
      if (!hit) {
        setSelected([]);
        return setDrag({ kind: "marquee", x0: x, y0: y, x1: x, y1: y });
      }
      const ids = ev.shiftKey ? [...new Set([...selected, hit.id])] : selected.includes(hit.id) ? selected : [hit.id];
      setSelected(ids);
      const start = new Map<string, El>();
      for (const id of ids) {
        const e = session.elements.get(id);
        if (!e) continue;
        start.set(id, e);
        const t = boundText(e, session.elements);
        if (t) start.set(t.id, t);
      }
      return setDrag({ kind: "move", x0: x, y0: y, start });
    }
    // Creating: shapes, lines, arrows, freedraw.
    const type: ElementType = tool === "custom" || tool === "note" ? "rectangle" : (tool as ElementType);
    const extra: Partial<El> =
      tool === "custom" ? { customData: { shape: custom } } : tool === "note" ? { fillStyle: "solid", roughness: 0, backgroundColor: NOTE_YELLOW, strokeColor: "#1e1e1e" } : {};
    const e = create(type, { x, y }, style, session.elements.values(), extra);
    if (type === "arrow" || type === "line") {
      const target = at(x, y);
      if (target && target.type !== "arrow" && target.type !== "line" && type === "arrow") e.startBinding = { elementId: target.id, focus: 0, gap: 4 };
    }
    session.elements.set(e.id, e);
    setDrag({ kind: "create", id: e.id, x0: x, y0: y });
  }

  function move(ev: RPointerEvent<SVGSVGElement>) {
    const [x, y] = toCanvas(ev.clientX, ev.clientY);
    session.presenceAt(x, y, selected, drag?.kind === "laser" ? drag.points : undefined);
    if (!drag) return;
    switch (drag.kind) {
      case "pan":
        setView({ ...drag.view, x: drag.view.x - (ev.clientX - drag.sx) / view.zoom, y: drag.view.y - (ev.clientY - drag.sy) / view.zoom });
        break;
      case "laser":
        drag.points.push([x, y]);
        setLaser([...drag.points.slice(-64)]);
        break;
      case "erase": {
        const hit = at(x, y);
        if (hit) remove([hit.id]);
        break;
      }
      case "marquee":
        setDrag({ ...drag, x1: x, y1: y });
        break;
      case "create": {
        const e = session.elements.get(drag.id)!;
        let next: El;
        if (e.type === "freedraw") next = { ...e, points: [...(e.points ?? []), [x - e.x, y - e.y]] };
        else if (e.type === "arrow" || e.type === "line") next = { ...e, points: [[0, 0], [x - e.x, y - e.y]], width: Math.abs(x - e.x), height: Math.abs(y - e.y) };
        else {
          const w = x - drag.x0;
          const h = y - drag.y0;
          const square = ev.shiftKey ? Math.max(Math.abs(w), Math.abs(h)) : 0;
          next = { ...e, x: Math.min(drag.x0, drag.x0 + (square ? Math.sign(w) * square : w)), y: Math.min(drag.y0, drag.y0 + (square ? Math.sign(h) * square : h)), width: square || Math.abs(w), height: square || Math.abs(h) };
        }
        session.elements.set(e.id, next);
        session.draft([next]);
        tick();
        break;
      }
      case "move": {
        const dx = x - drag.x0;
        const dy = y - drag.y0;
        const moved = [...drag.start.values()].map((e) => ({ ...e, x: e.x + dx, y: e.y + dy }));
        for (const m of moved) session.elements.set(m.id, m);
        session.draft(moved);
        tick();
        break;
      }
      case "resize": {
        const s = drag.start;
        const b = bounds(s);
        const minX = drag.corner.includes("w") ? Math.min(x, b.maxX - 4) : b.minX;
        const maxX = drag.corner.includes("e") ? Math.max(x, b.minX + 4) : b.maxX;
        const minY = drag.corner.includes("n") ? Math.min(y, b.maxY - 4) : b.minY;
        const maxY = drag.corner.includes("s") ? Math.max(y, b.minY + 4) : b.maxY;
        let next: El = { ...s, x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        if (s.points && (s.type === "freedraw" || s.type === "line" || s.type === "arrow")) {
          const sx = (maxX - minX) / Math.max(1, b.maxX - b.minX);
          const sy = (maxY - minY) / Math.max(1, b.maxY - b.minY);
          next = { ...s, x: minX + (s.x - b.minX) * sx, y: minY + (s.y - b.minY) * sy, points: s.points.map(([px, py]) => [px * sx, py * sy]) };
        }
        session.elements.set(s.id, next);
        session.draft([next]);
        tick();
        break;
      }
    }
  }

  function up() {
    if (!drag) return;
    if (drag.kind === "create") {
      const e = session.elements.get(drag.id)!;
      const tiny = e.type !== "freedraw" && Math.abs(e.width) < 3 && Math.abs(e.height) < 3;
      session.elements.delete(e.id);
      if (!tiny) {
        let final = e;
        if (e.type === "arrow" && e.points) {
          const end = e.points[e.points.length - 1]!;
          const target = at(e.x + end[0], e.y + end[1]);
          if (target && target.id !== e.id && target.type !== "arrow" && target.type !== "line") final = { ...final, endBinding: { elementId: target.id, focus: 0, gap: 4 } };
        }
        const bound = [final.startBinding?.elementId, final.endBinding?.elementId].filter((x): x is string => Boolean(x)).map((bid) => session.elements.get(bid)).filter((x): x is El => Boolean(x));
        commit([final, ...bound.map((b) => bump(b, { boundElements: [...(b.boundElements ?? []), { id: final.id, type: "arrow" }] }))]);
        if (final.type !== "freedraw") {
          setSelected([final.id]);
          setTool("select");
        }
      }
    } else if (drag.kind === "move" || drag.kind === "resize") {
      const ids = drag.kind === "move" ? [...drag.start.keys()] : [drag.id];
      const starts = drag.kind === "move" ? drag.start : new Map([[drag.id, drag.start]]);
      const finals = ids.map((id) => {
        const now = session.elements.get(id)!;
        const start = starts.get(id)!;
        session.elements.set(id, start);
        return bump(start, { x: now.x, y: now.y, width: now.width, height: now.height, points: now.points });
      });
      if (finals.some((f, i) => f.x !== starts.get(ids[i]!)!.x || f.y !== starts.get(ids[i]!)!.y || f.width !== starts.get(ids[i]!)!.width || f.height !== starts.get(ids[i]!)!.height)) {
        commit([...finals, ...followArrows(finals)]);
      } else tick();
    } else if (drag.kind === "marquee") {
      const minX = Math.min(drag.x0, drag.x1), maxX = Math.max(drag.x0, drag.x1), minY = Math.min(drag.y0, drag.y1), maxY = Math.max(drag.y0, drag.y1);
      setSelected(elements.filter((e) => !e.containerId && (() => { const b = bounds(e); return b.minX >= minX && b.maxX <= maxX && b.minY >= minY && b.maxY <= maxY; })()).map((e) => e.id));
    } else if (drag.kind === "laser") {
      setTimeout(() => setLaser([]), 1000);
    }
    setDrag(null);
  }

  /** Arrows bound to moved shapes follow them: their ends go to the shapes' new edges. */
  function followArrows(moved: El[]): El[] {
    const ids = new Set(moved.map((m) => m.id));
    const shapeOf = (id: string) => moved.find((m) => m.id === id) ?? session.elements.get(id);
    const out: El[] = [];
    for (const a of session.elements.values()) {
      if (a.type !== "arrow" || a.isDeleted || ids.has(a.id)) continue;
      const s = a.startBinding && ids.has(a.startBinding.elementId) ? shapeOf(a.startBinding.elementId) : undefined;
      const t = a.endBinding && ids.has(a.endBinding.elementId) ? shapeOf(a.endBinding.elementId) : undefined;
      if (!s && !t) continue;
      const pts = (a.points ?? [[0, 0]]).map(([x, y]) => [x + a.x, y + a.y] as [number, number]);
      const start = s ? edgePoint(s, pts[pts.length - 1]!) : pts[0]!;
      const end = t ? edgePoint(t, start) : pts[pts.length - 1]!;
      const startFinal = s ? edgePoint(s, end) : start;
      out.push(bump(a, { x: startFinal[0], y: startFinal[1], points: [[0, 0], [end[0] - startFinal[0], end[1] - startFinal[1]]], width: Math.abs(end[0] - startFinal[0]), height: Math.abs(end[1] - startFinal[1]) }));
    }
    return out;
  }

  // ---- text ----
  function finishText(value?: string) {
    const id = editing;
    setEditing(null);
    if (!id) return;
    const e = session.elements.get(id);
    const text = value ?? (document.getElementById("board-text-editor") as HTMLTextAreaElement | null)?.value ?? e?.text ?? "";
    if (!e) return;
    if (!text.trim()) return remove([id]);
    const size = e.fontSize ?? 20;
    const lines = text.split("\n");
    const width = Math.max(...lines.map((l) => l.length)) * size * 0.55 + 4;
    commit([bump(e, { text, originalText: text, width: e.containerId ? e.width : width, height: lines.length * size * 1.25 })]);
  }

  function editShapeText(shape: El) {
    const existing = boundText(shape, session.elements);
    if (existing) return setEditing(existing.id);
    const t = create("text", { x: shape.x + 8, y: shape.y + shape.height / 2 - 12, width: shape.width - 16, height: 25 }, styleOf(shape), session.elements.values(), { containerId: shape.id, textAlign: "center", verticalAlign: "middle" });
    commit([t, bump(shape, { boundElements: [...(shape.boundElements ?? []), { id: t.id, type: "text" }] })]);
    setEditing(t.id);
  }

  // ---- images: an image message with board_id, then an image element with its id ----
  async function addImage(file: File, x?: number, y?: number) {
    if (!session.board || !file.type.startsWith("image/")) return;
    try {
      const sent = await services.sendCanvasImage(session.board.conversation_id, session.id, file);
      const ratio = (sent.response.media?.width ?? 4) / (sent.response.media?.height ?? 3);
      const w = ratio >= 1 ? 320 : 320 * ratio;
      const h = ratio >= 1 ? 320 / ratio : 320;
      const r = svg.current!.getBoundingClientRect();
      const cx = x ?? view.x + r.width / view.zoom / 2;
      const cy = y ?? view.y + r.height / view.zoom / 2;
      const e = create("image", { x: cx - w / 2, y: cy - h / 2, width: w, height: h }, style, session.elements.values(), { fileId: sent.response.id });
      commit([e]);
      setSelected([e.id]);
    } catch {
      session.error = "Das Bild ging nicht aufs Canvas.";
      tick();
    }
    setTool("select");
  }

  // ---- keys ----
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (editing || (e.target instanceof HTMLElement && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName))) return;
      const mod = e.ctrlKey || e.metaKey;
      if (e.code === "Space") {
        spaceDown.current = true;
        e.preventDefault();
        return;
      }
      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) session.redoLast();
        else session.undoLast();
        return;
      }
      if (mod && e.key.toLowerCase() === "y") return void (e.preventDefault(), session.redoLast());
      if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        const copies = selected.map((id) => session.elements.get(id)).filter((x): x is El => Boolean(x)).map((s) => create(s.type, { x: s.x + 16, y: s.y + 16, width: s.width, height: s.height }, styleOf(s), session.elements.values(), { ...s, id: undefined as never, x: s.x + 16, y: s.y + 16, boundElements: null, version: 1 }));
        for (const c of copies) c.id = crypto.randomUUID().replace(/-/g, "").slice(0, 21);
        commit(copies);
        setSelected(copies.map((c) => c.id));
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selected.length) return remove(selected);
      if (e.key === "Escape") return setSelected([]);
      if (e.key === "Enter" && selected.length === 1) {
        const s = session.elements.get(selected[0]!);
        if (s?.type === "text") setEditing(s.id);
        else if (s && ["rectangle", "ellipse", "diamond"].includes(s.type)) editShapeText(s);
        return;
      }
      if (mod) return;
      const t = TOOLS.find((x) => x.key === e.key.toLowerCase());
      if (t) setTool(t.tool);
      else if (e.key.toLowerCase() === "d") setTool("diamond");
      else if (e.key.toLowerCase() === "l") setTool("line");
      else if (e.key.toLowerCase() === "k") setTool("laser");
      else if (e.key.toLowerCase() === "n") setTool("note");
    };
    const upKey = (e: KeyboardEvent) => e.code === "Space" && (spaceDown.current = false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", upKey);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", upKey);
    };
  });

  // Wheel: pan; with Ctrl/Cmd (and pinch): zoom around the pointer.
  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      setView((v) => {
        if (e.ctrlKey || e.metaKey) {
          const r = el.getBoundingClientRect();
          const zoom = Math.min(4, Math.max(0.1, v.zoom * Math.exp(-e.deltaY / 300)));
          const px = v.x + (e.clientX - r.left) / v.zoom;
          const py = v.y + (e.clientY - r.top) / v.zoom;
          return { zoom, x: px - (e.clientX - r.left) / zoom, y: py - (e.clientY - r.top) / zoom };
        }
        return { ...v, x: v.x + e.deltaX / v.zoom, y: v.y + e.deltaY / v.zoom };
      });
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);

  const [, setTick] = useState(0);
  const tick = () => setTick((n) => n + 1);

  const selection = selected.map((id) => byId.get(id)).filter((e): e is El => Boolean(e));
  const selBox = union(selection.map(bounds));
  const editingEl = editing ? session.elements.get(editing) : undefined;
  const r = svg.current?.getBoundingClientRect();
  const width = r?.width ?? 1200;
  const height = r?.height ?? 800;
  const suggestion = session.board?.suggestion;
  const names = (id: string) => label(conversation?.members.find((m) => m.user.id === id)?.user);

  return (
    <main className={styles.board} aria-label="Canvas">
      <header className={styles.top}>
        <button className={styles.iconButton} onClick={() => onBack(session.board?.conversation_id)} aria-label="Zurück">
          <Icon name="back" />
        </button>
        <div className={styles.title}>
          <b
            title="Klicken zum Umbenennen"
            onClick={async () => {
              const t = prompt("Name des Canvas", session.board?.title ?? "")?.trim();
              if (t) await services.session.call(`/v1/boards/${session.id}`, { method: "PATCH", json: { title: t } }).then(() => session.open()).catch(() => undefined);
            }}
          >
            {session.board?.title ?? "Canvas"}
          </b>
          <span>{conversation ? `${conversation.title ?? names(conversation.members.find((m) => m.user.id !== me)?.user.id ?? "")}` : ""}{session.error ? ` · ${session.error}` : " · gespeichert"}</span>
        </div>
        <div className={styles.present}>
          {session.present.filter((u) => u !== me).map((u) => (
            <span key={u} title={`${names(u)} ist hier`}>
              <Avatar id={u} name={names(u)} size={30} />
            </span>
          ))}
        </div>
        <button className={styles.textButton} onClick={() => setMenu(menu === "versions" ? null : "versions")}>
          Versionen
        </button>
        <button className={styles.outlined} onClick={() => setMenu(menu === "export" ? null : "export")}>
          <Icon name="open" size={18} /> Exportieren
        </button>
        <button className={styles.iconButton} onClick={() => onBack(session.board?.conversation_id)} aria-label="Zum Chat" title="Zum Chat (dort mit „Canvas“ ansprechen)">
          <Icon name="chat" />
        </button>
      </header>

      <div className={styles.tools} role="toolbar" aria-label="Werkzeuge">
        {TOOLS.slice(0, 4).map((t) => (
          <ToolButton key={t.tool} t={t} active={tool === t.tool} onClick={() => setTool(t.tool)} />
        ))}
        <button className={tool === "custom" || tool === "diamond" || tool === "note" || tool === "line" ? `${styles.tool} ${styles.toolOn}` : styles.tool} onClick={() => setMenu(menu === "shapes" ? null : "shapes")} aria-label="Weitere Formen" title="Weitere Formen">
          <Icon name="add" />
        </button>
        {TOOLS.slice(4).map((t) => (
          <ToolButton key={t.tool} t={t} active={tool === t.tool} onClick={() => setTool(t.tool)} />
        ))}
        <button className={tool === "laser" ? `${styles.tool} ${styles.toolOn}` : styles.tool} onClick={() => setTool("laser")} aria-label="Zeigestift" title="Zeigestift (K)">
          <span style={{ color: "#FF4D79", fontSize: 20 }}>●</span>
        </button>
        <input ref={picker} type="file" accept="image/*" hidden onChange={(e) => (e.target.files?.[0] && void addImage(e.target.files[0]), (e.target.value = ""))} />
      </div>

      {menu === "shapes" && (
        <div className={styles.shapeMenu}>
          {(["diamond", "line", "note", ...CUSTOM_SHAPES] as string[]).map((s) => (
            <button
              key={s}
              onClick={() => {
                if (s === "diamond" || s === "line" || s === "note") setTool(s as Tool);
                else (setCustom(s), setTool("custom"));
                setMenu(null);
              }}
            >
              {SHAPE_NAMES[s]}
            </button>
          ))}
        </div>
      )}

      <svg
        ref={svg}
        className={styles.canvas}
        style={{ cursor: tool === "hand" || drag?.kind === "pan" ? "grab" : tool === "select" ? "default" : "crosshair" }}
        viewBox={`${view.x} ${view.y} ${width / view.zoom} ${height / view.zoom}`}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onDoubleClick={(ev) => {
          const [x, y] = toCanvas(ev.clientX, ev.clientY);
          const hit = at(x, y);
          if (hit?.type === "text") setEditing(hit.id);
          else if (hit && ["rectangle", "ellipse", "diamond"].includes(hit.type)) editShapeText(hit);
          else if (!hit) {
            const e = create("text", { x, y }, style, session.elements.values());
            commit([e]);
            setEditing(e.id);
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) {
            const [x, y] = toCanvas(e.clientX, e.clientY);
            void addImage(f, x, y);
          }
        }}
      >
        {elements.map((e) => (
          <ElementView key={e.id} e={e} color={color} hidden={e.id === editing} conversationId={session.board?.conversation_id} />
        ))}
        {(suggestion?.elements ?? []).map((e) => (
          <g key={`s${(e as El).id}`} opacity={0.75} className={(e as El).isDeleted ? styles.suggestRemoved : styles.suggestAdded}>
            {paths({ ...(e as El), isDeleted: false, roughness: 0 }).map((p, i) => (
              <path key={i} d={p.d} fill="none" stroke={(e as El).isDeleted ? "#C92A2A" : "#7048e8"} strokeWidth={2} strokeDasharray="8 6" />
            ))}
          </g>
        ))}
        {selBox && (
          <g>
            <rect x={selBox.minX - 4} y={selBox.minY - 4} width={selBox.maxX - selBox.minX + 8} height={selBox.maxY - selBox.minY + 8} fill="none" stroke="#5e01ce" strokeWidth={1.5 / view.zoom} strokeDasharray={`${6 / view.zoom} ${4 / view.zoom}`} />
            {selection.length === 1 &&
              (
                [
                  ["nw", selBox.minX - 4, selBox.minY - 4],
                  ["ne", selBox.maxX + 4, selBox.minY - 4],
                  ["sw", selBox.minX - 4, selBox.maxY + 4],
                  ["se", selBox.maxX + 4, selBox.maxY + 4],
                ] as const
              ).map(([c, x, y]) => <rect key={c} data-handle={c} x={x - 5 / view.zoom} y={y - 5 / view.zoom} width={10 / view.zoom} height={10 / view.zoom} fill="#fff" stroke="#5e01ce" strokeWidth={1.5 / view.zoom} style={{ cursor: `${c}-resize` }} />)}
          </g>
        )}
        {drag?.kind === "marquee" && <rect x={Math.min(drag.x0, drag.x1)} y={Math.min(drag.y0, drag.y1)} width={Math.abs(drag.x1 - drag.x0)} height={Math.abs(drag.y1 - drag.y0)} fill="rgb(94 1 206 / 0.06)" stroke="#5e01ce" strokeWidth={1 / view.zoom} />}
        {laser.length > 1 && <polyline points={laser.map((p) => p.join(",")).join(" ")} fill="none" stroke="#FF4D79" strokeWidth={4 / view.zoom} strokeLinecap="round" opacity={0.85} />}
        {[...session.presence.values()].filter((p) => p.user !== me).map((p) => {
          const c = colorFor(p.user);
          return (
            <g key={p.user}>
              {(p.selection ?? []).map((id) => {
                const e = byId.get(id);
                if (!e) return null;
                const b = bounds(e);
                return <rect key={id} x={b.minX - 6} y={b.minY - 6} width={b.maxX - b.minX + 12} height={b.maxY - b.minY + 12} fill="none" stroke={c} strokeWidth={1.5 / view.zoom} />;
              })}
              {p.laser && p.laser.length > 1 && <polyline points={p.laser.map((q) => q.join(",")).join(" ")} fill="none" stroke="#FF4D79" strokeWidth={4 / view.zoom} opacity={0.85} />}
              {p.x !== undefined && (
                <g transform={`translate(${p.x} ${p.y}) scale(${1 / view.zoom})`}>
                  <path d="M0 0 L0 22 L6 16 L11 27 L15 25 L10 14 L18 14 Z" fill={c} stroke="#fff" strokeWidth={1} />
                  <rect x={14} y={24} rx={6} width={names(p.user).length * 7.5 + 12} height={20} fill={c} />
                  <text x={20} y={38} fontSize={13} fill="#fff" fontFamily="Roboto, sans-serif" fontWeight={500}>
                    {names(p.user).split(" ")[0]}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {editingEl && <TextEditor e={editingEl} view={view} onDone={finishText} container={editingEl.containerId ? session.elements.get(editingEl.containerId) : undefined} />}

      <StylePanel
        selection={selection}
        style={style}
        onStyle={(s) => {
          setStyle(s);
          if (selection.length)
            commit(
              selection.map((e) =>
                bump(e, e.type === "text" ? { fontFamily: s === "sketch" ? FONT_EXCALIFONT : 2 } : { roughness: s === "sketch" ? 1 : 0, fillStyle: e.fillStyle === "none" ? "none" : s === "sketch" ? "hachure" : "solid", roundness: s === "clean" && e.type !== "ellipse" ? { type: 3 } : null }),
              ),
            );
        }}
        onPatch={setOnSelection}
      />

      {suggestion && (
        <div className={styles.suggestion} role="dialog" aria-label="Vorschlag von Voxie">
          <b>
            <Icon name="sparkle" size={18} /> Voxie schlägt vor
          </b>
          <p>{suggestion.explanation}</p>
          <div className={styles.chips}>
            {(suggestion.added ?? []).map((a, i) => (
              <span key={`a${i}`} className={styles.added}>
                + {a}
              </span>
            ))}
            {(suggestion.removed ?? []).map((a, i) => (
              <span key={`r${i}`} className={styles.removed}>
                − {a}
              </span>
            ))}
          </div>
          <div>
            <button className={styles.primary} onClick={() => void decide("apply")}>
              Übernehmen
            </button>
            <button className={styles.textButton} onClick={() => void decide("dismiss")}>
              Verwerfen
            </button>
          </div>
        </div>
      )}

      {menu === "export" && <ExportMenu session={session} elements={elements} onClose={() => setMenu(null)} />}
      {menu === "versions" && <Versions session={session} names={names} onClose={() => setMenu(null)} />}

      <div className={styles.bottom}>
        <span className={styles.hint}>
          <kbd>Space</kbd> + Ziehen: verschieben · <kbd>⌘ Z</kbd> rückgängig · <kbd>⌘ D</kbd> duplizieren · Doppelklick: Text · Bilder hineinziehen
        </span>
        <div className={styles.zoom}>
          <button className={styles.iconButton} onClick={() => session.undoLast()} disabled={!session.canUndo()} aria-label="Rückgängig">
            <Icon name="undo" />
          </button>
          <button className={styles.iconButton} onClick={() => session.redoLast()} disabled={!session.canRedo()} aria-label="Wiederholen">
            <Icon name="redo" />
          </button>
          <button className={styles.textButton} onClick={() => setView((v) => ({ ...v, zoom: 1 }))}>
            {Math.round(view.zoom * 100)} %
          </button>
        </div>
      </div>
    </main>
  );

  async function decide(what: "apply" | "dismiss") {
    if (!suggestion) return;
    await services.session.call(`/v1/boards/${session.id}/suggestions/${suggestion.id}/${what}`, { method: "POST" }).catch(() => (session.error = "Das Canvas hat sich inzwischen geändert."));
    await session.open().catch(() => undefined);
  }
}

function ToolButton({ t, active, onClick }: { t: (typeof TOOLS)[number]; active: boolean; onClick: () => void }) {
  return (
    <button className={active ? `${styles.tool} ${styles.toolOn}` : styles.tool} onClick={onClick} aria-label={t.label} aria-pressed={active} title={`${t.label} (${t.key.toUpperCase()})`}>
      <Icon name={t.icon} />
      <span className={styles.key}>{t.key.toUpperCase()}</span>
    </button>
  );
}

/** Where the line from the centre of [shape] towards [toward] leaves its box (arrow docking, simplified). */
function edgePoint(shape: El, toward: [number, number]): [number, number] {
  const b = bounds(shape);
  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  const dx = toward[0] - cx;
  const dy = toward[1] - cy;
  if (dx === 0 && dy === 0) return [cx, cy];
  const sx = dx === 0 ? Infinity : (b.maxX - b.minX) / 2 / Math.abs(dx);
  const sy = dy === 0 ? Infinity : (b.maxY - b.minY) / 2 / Math.abs(dy);
  const s = Math.min(sx, sy);
  return [cx + dx * s + Math.sign(dx) * 4, cy + dy * s + Math.sign(dy) * 4];
}

function ElementView({ e, color, hidden, conversationId }: { e: El; color: (c: string) => string; hidden: boolean; conversationId?: string }) {
  const b = bounds(e);
  const rotate = e.angle ? `rotate(${(e.angle * 180) / Math.PI} ${(b.minX + b.maxX) / 2} ${(b.minY + b.maxY) / 2})` : undefined;
  const opacity = (e.opacity ?? 100) / 100;
  if (hidden) return null;
  if (e.type === "text") {
    const size = e.fontSize ?? 20;
    const family = e.fontFamily === FONT_EXCALIFONT ? "Excalifont, Caveat, cursive" : "Roboto, sans-serif";
    const lines = (e.text ?? "").split("\n");
    const anchor = e.textAlign === "center" ? "middle" : e.textAlign === "right" ? "end" : "start";
    const x = e.textAlign === "center" ? b.minX + (b.maxX - b.minX) / 2 : e.textAlign === "right" ? b.maxX : b.minX;
    return (
      <g transform={rotate} opacity={opacity}>
        <text x={x} y={b.minY} fontSize={size} fontFamily={family} fill={color(e.strokeColor ?? "#1e1e1e")} textAnchor={anchor}>
          {lines.map((l, i) => (
            <tspan key={i} x={x} dy={i === 0 ? size : size * (e.lineHeight ?? 1.25)}>
              {l || " "}
            </tspan>
          ))}
        </text>
      </g>
    );
  }
  if (e.type === "image") return <ImageView e={e} conversationId={conversationId} transform={rotate} />;
  return (
    <g transform={rotate} opacity={opacity}>
      {paths(e, color).map((p, i) => (
        <path key={i} d={p.d} stroke={p.stroke} strokeWidth={p.strokeWidth} fill={p.fill} strokeDasharray={p.dash} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </g>
  );
}

/** An image element: the picture of the image message its fileId names, decrypted like in the chat. */
function ImageView({ e, conversationId, transform }: { e: El; conversationId?: string; transform?: string }) {
  const services = useServices();
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!e.fileId || !conversationId) return;
    let alive = true;
    (async () => {
      let m = services.store.message(conversationId, e.fileId!);
      if (!m) {
        await services.sync.loadMessages(conversationId).catch(() => undefined);
        m = services.store.message(conversationId, e.fileId!);
      }
      if (!m) return;
      const u = await mediaUrl(m, m.response.media?.thumb_url ? "thumb" : "media", () => services.refresh(m!)).catch(() => undefined);
      if (alive) setUrl(u);
    })();
    return () => {
      alive = false;
    };
  }, [e.fileId, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps
  const b = bounds(e);
  return url ? (
    <image href={url} x={b.minX} y={b.minY} width={b.maxX - b.minX} height={b.maxY - b.minY} preserveAspectRatio="none" transform={transform} />
  ) : (
    <rect x={b.minX} y={b.minY} width={b.maxX - b.minX} height={b.maxY - b.minY} fill="#e9ecef" stroke="#ced4da" transform={transform} />
  );
}

function TextEditor({ e, view, onDone, container }: { e: El; view: View; onDone: (value: string) => void; container?: El }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const box = container ? bounds(container) : bounds(e);
  const size = (e.fontSize ?? 20) * view.zoom;
  return (
    <textarea
      id="board-text-editor"
      ref={ref}
      className={styles.textEditor}
      defaultValue={e.text ?? ""}
      style={{
        // The canvas starts 64 px below the top of the board (the header).
        left: (box.minX - view.x) * view.zoom,
        top: 64 + ((container ? (box.minY + box.maxY) / 2 - (e.fontSize ?? 20) * 0.7 : box.minY) - view.y) * view.zoom,
        width: Math.max(160, (box.maxX - box.minX) * view.zoom),
        fontSize: size,
        fontFamily: e.fontFamily === FONT_EXCALIFONT ? "Excalifont, cursive" : "Roboto, sans-serif",
        textAlign: (e.textAlign as "left" | "center") ?? "left",
      }}
      onKeyDown={(ev) => {
        if (ev.key === "Escape" || (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey))) onDone(ev.currentTarget.value);
      }}
      onBlur={(ev) => onDone(ev.currentTarget.value)}
    />
  );
}

function StylePanel({ selection, style, onStyle, onPatch }: { selection: El[]; style: Style; onStyle: (s: Style) => void; onPatch: (p: Partial<El>) => void }) {
  const one = selection[0];
  return (
    <div className={styles.stylePanel} aria-label="Stil">
      <div className={styles.row}>
        <button className={style === "sketch" ? styles.chipOn : styles.chip} onClick={() => onStyle("sketch")}>
          Skizze
        </button>
        <button className={style === "clean" ? styles.chipOn : styles.chip} onClick={() => onStyle("clean")}>
          Sauber
        </button>
      </div>
      {one && (
        <>
          <small>Linie</small>
          <div className={styles.swatches}>
            {STROKES.map((c) => (
              <button key={c} style={{ background: c }} className={one.strokeColor === c ? styles.swatchOn : undefined} onClick={() => onPatch({ strokeColor: c })} aria-label={`Linienfarbe ${c}`} />
            ))}
          </div>
          {one.type !== "text" && one.type !== "line" && one.type !== "arrow" && one.type !== "freedraw" && (
            <>
              <small>Füllung</small>
              <div className={styles.swatches}>
                {FILLS.map((c) => (
                  <button key={c} style={{ background: c === "transparent" ? "repeating-conic-gradient(#ddd 0 25%, #fff 0 50%) 0 0/10px 10px" : c }} className={one.backgroundColor === c ? styles.swatchOn : undefined} onClick={() => onPatch({ backgroundColor: c })} aria-label={`Füllung ${c}`} />
                ))}
              </div>
            </>
          )}
          {one.type !== "text" && (
            <div className={styles.row}>
              {[1, 2, 4].map((w) => (
                <button key={w} className={one.strokeWidth === w ? styles.chipOn : styles.chip} onClick={() => onPatch({ strokeWidth: w })}>
                  {w === 1 ? "dünn" : w === 2 ? "mittel" : "dick"}
                </button>
              ))}
              <button className={one.strokeStyle === "dashed" ? styles.chipOn : styles.chip} onClick={() => onPatch({ strokeStyle: one.strokeStyle === "dashed" ? "solid" : "dashed" })}>
                gestrichelt
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ExportMenu({ session, elements, onClose }: { session: BoardSession; elements: El[]; onClose: () => void }) {
  const services = useServices();
  const name = (session.board?.title ?? "Canvas").replace(/[^\p{L}\p{N} _-]/gu, "");
  const download = (blob: Blob, file: string) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = file;
    a.click();
  };
  function svgText(): string {
    const box = union(elements.map(bounds)) ?? { minX: 0, minY: 0, maxX: 100, maxY: 100 };
    const live = document.querySelector("svg[viewBox]") as SVGSVGElement | null;
    const clone = live?.cloneNode(true) as SVGSVGElement;
    clone.querySelectorAll("rect[data-handle]").forEach((r) => r.remove());
    clone.setAttribute("viewBox", `${box.minX - 24} ${box.minY - 24} ${box.maxX - box.minX + 48} ${box.maxY - box.minY + 48}`);
    clone.setAttribute("width", String(box.maxX - box.minX + 48));
    clone.setAttribute("height", String(box.maxY - box.minY + 48));
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.insertAdjacentHTML("afterbegin", `<rect x="${box.minX - 24}" y="${box.minY - 24}" width="${box.maxX - box.minX + 48}" height="${box.maxY - box.minY + 48}" fill="#ffffff"/>`);
    return new XMLSerializer().serializeToString(clone);
  }
  async function png() {
    const text = svgText();
    const img = new Image();
    img.src = URL.createObjectURL(new Blob([text], { type: "image/svg+xml" }));
    await img.decode();
    const canvas = document.createElement("canvas");
    canvas.width = img.width * 2;
    canvas.height = img.height * 2;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(2, 2);
    ctx.drawImage(img, 0, 0);
    canvas.toBlob((b) => b && download(b, `${name}.png`), "image/png");
  }
  async function excalidraw() {
    const token = await services.session.token();
    const r = await fetch(`/v1/boards/${session.id}/export`, { headers: { authorization: `Bearer ${token}` } });
    if (r.ok) download(await r.blob(), `${name}.excalidraw`);
  }
  return (
    <div className={styles.menu} role="menu">
      <button onClick={() => (void png(), onClose())}>PNG</button>
      <button onClick={() => (download(new Blob([svgText()], { type: "image/svg+xml" }), `${name}.svg`), onClose())}>SVG</button>
      <button onClick={() => (void excalidraw(), onClose())}>.excalidraw</button>
    </div>
  );
}

function Versions({ session, names, onClose }: { session: BoardSession; names: (id: string) => string; onClose: () => void }) {
  const services = useServices();
  const [list, setList] = useState<BoardSnapshotListResponse["snapshots"]>();
  useEffect(() => void services.session.call<BoardSnapshotListResponse>(`/v1/boards/${session.id}/snapshots`).then((r) => setList(r.snapshots), () => setList([])), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`${styles.menu} ${styles.versions}`} role="menu">
      <b>Versionen</b>
      {!list && <small>Wird geladen …</small>}
      {list?.length === 0 && <small>Noch keine älteren Versionen.</small>}
      {list?.map((v) => (
        <button
          key={v.seq}
          onClick={async () => {
            if (!confirm("Diese Version wiederherstellen? Das Canvas wird für alle so wie damals.")) return;
            await services.session.call(`/v1/boards/${session.id}/restore`, { method: "POST", json: { seq: v.seq } }).catch(() => undefined);
            await session.open().catch(() => undefined);
            onClose();
          }}
        >
          {shortTime(v.created_at)} · {names(v.author_id)} · {v.element_count} Elemente
        </button>
      ))}
    </div>
  );
}

export type { MessageResponse };
